import React, { useEffect } from 'react';

const TimeSeriesDatabases = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Time-Series Databases - Efficient Time-Based Data Storage 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master time-series databases with InfluxDB, Prometheus, and TimescaleDB. Learn storage patterns, querying, and retention strategies.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master time-series databases with InfluxDB, Prometheus, and TimescaleDB. Learn storage patterns, querying, and retention strategies.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Time-Series Databases: Efficient Time-Based Data Storage</h1>
        <p style={styles.subtitle}>
          Store and query time-stamped data efficiently with specialized database architectures
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Introduction to Time-Series Data</h2>
          <p style={styles.paragraph}>
            Time-series data consists of data points indexed by time, typically collected at regular 
            intervals. This type of data is fundamental to monitoring systems, IoT applications, 
            financial markets, and analytics platforms. Time-series databases are specialized storage 
            systems optimized for handling the unique characteristics and query patterns of time-based data.
          </p>
          <p style={styles.paragraph}>
            Unlike traditional relational databases, time-series databases are designed for high write 
            throughput, efficient storage compression, and fast time-range queries. They excel at handling 
            massive volumes of timestamped data while providing the analytical capabilities needed for 
            real-time monitoring and historical analysis.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Time-Series Data Characteristics</h2>
          
          <h3 style={styles.heading}>Key Properties and Patterns</h3>
          <p style={styles.paragraph}>
            Time-series data has unique characteristics that differentiate it from other data types. 
            Understanding these properties is crucial for designing effective storage and query strategies.
          </p>
          <pre style={styles.codeBlock}>
{`Time-Series Data Characteristics:

1. TIME-ORDERED DATA
┌─────────────────────────────────────────────────────────────┐
│ Timestamp    │ Metric       │ Value    │ Tags              │
├─────────────────────────────────────────────────────────────┤
│ 2024-01-15   │ cpu_usage    │ 85.2     │ host=web1,env=prod│
│ 10:00:00     │              │          │                   │
├─────────────────────────────────────────────────────────────┤
│ 2024-01-15   │ cpu_usage    │ 87.1     │ host=web1,env=prod│
│ 10:00:15     │              │          │                   │
├─────────────────────────────────────────────────────────────┤
│ 2024-01-15   │ cpu_usage    │ 82.5     │ host=web1,env=prod│
│ 10:00:30     │              │          │                   │
└─────────────────────────────────────────────────────────────┘

2. HIGH WRITE VOLUME
- Continuous data ingestion (millions of points/second)
- Write-heavy workloads (90% writes, 10% reads)
- Batch and streaming ingestion patterns
- Minimal updates/deletes (mostly append-only)

3. TIME-RANGE QUERIES
- Most queries involve time ranges
- Recent data accessed more frequently
- Aggregation queries (avg, sum, max over time periods)
- Downsampling for historical data

4. DATA RETENTION PATTERNS
- Recent data: high resolution (1-second intervals)
- Medium-term: reduced resolution (1-minute intervals)
- Long-term: aggregated data (1-hour intervals)
- Automatic data expiration based on age

Common Time-Series Use Cases:

Monitoring & Observability:
├─ System metrics (CPU, memory, disk, network)
├─ Application metrics (request rate, latency, errors)
├─ Business metrics (revenue, user activity, conversions)
└─ Infrastructure monitoring (containers, services)

IoT & Sensor Data:
├─ Temperature, humidity, pressure sensors
├─ GPS tracking and location data
├─ Industrial equipment monitoring
└─ Smart home and building automation

Financial Data:
├─ Stock prices and trading volumes
├─ Market data feeds
├─ Risk metrics and portfolio analytics
└─ Cryptocurrency price tracking

Analytics & Business Intelligence:
├─ User behavior tracking
├─ Performance analytics
├─ A/B testing results
└─ Operational dashboards

Time-Series Data Model:

Measurement: The thing being measured (e.g., "cpu_usage")
Timestamp: When the measurement was taken
Value: The actual measurement value
Tags/Labels: Metadata for grouping and filtering
  - host=web1, environment=production, region=us-east

Example Data Point:
{
  "measurement": "http_requests_total",
  "timestamp": "2024-01-15T10:00:00Z",
  "value": 1547,
  "tags": {
    "method": "GET",
    "status": "200",
    "service": "api-gateway",
    "region": "us-east-1"
  }
}

Query Patterns:

1. Time Range Queries:
SELECT value FROM cpu_usage 
WHERE time >= '2024-01-15T09:00:00Z' 
  AND time <= '2024-01-15T10:00:00Z'
  AND host = 'web1'

2. Aggregation Queries:
SELECT MEAN(value) FROM cpu_usage 
WHERE time >= now() - 1h 
GROUP BY time(5m), host

3. Downsampling Queries:
SELECT MAX(value) as peak_cpu 
FROM cpu_usage 
WHERE time >= now() - 24h 
GROUP BY time(1h)

4. Multi-Series Queries:
SELECT MEAN(cpu_usage), MEAN(memory_usage) 
FROM system_metrics 
WHERE time >= now() - 1h 
GROUP BY time(5m)

Storage Challenges:

High Cardinality:
- Many unique tag combinations
- Can lead to storage explosion
- Requires careful schema design

Data Compression:
- Time-based compression algorithms
- Delta encoding for values
- Run-length encoding for repeated values

Hot vs Cold Data:
- Recent data needs fast access
- Historical data can be compressed more
- Tiered storage strategies`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Popular Time-Series Databases</h2>
          
          <h3 style={styles.heading}>InfluxDB Implementation</h3>
          <p style={styles.paragraph}>
            InfluxDB is a purpose-built time-series database that provides high-performance storage 
            and querying capabilities. It uses a SQL-like query language called InfluxQL and 
            supports advanced features like continuous queries and retention policies.
          </p>
          <pre style={styles.codeBlock}>
{`InfluxDB Implementation:

# InfluxDB Configuration
# influxdb.conf
[meta]
  dir = "/var/lib/influxdb/meta"

[data]
  dir = "/var/lib/influxdb/data"
  wal-dir = "/var/lib/influxdb/wal"
  
  # Cache settings
  cache-max-memory-size = "1g"
  cache-snapshot-memory-size = "25m"
  
  # Compaction settings
  compact-full-write-cold-duration = "4h"
  max-concurrent-compactions = 3
  
  # Write settings
  max-series-per-database = 1000000
  max-values-per-tag = 100000

[coordinator]
  write-timeout = "10s"
  max-concurrent-queries = 0
  query-timeout = "0s"

[retention]
  enabled = true
  check-interval = "30m"

[http]
  enabled = true
  bind-address = ":8086"
  max-body-size = "25000000"

# Python Client Implementation
from influxdb_client import InfluxDBClient, Point, WritePrecision
from influxdb_client.client.write_api import SYNCHRONOUS
import time
import random
from datetime import datetime, timezone
from typing import List, Dict, Any

class InfluxDBManager:
    def __init__(self, url: str, token: str, org: str):
        self.client = InfluxDBClient(url=url, token=token, org=org)
        self.write_api = self.client.write_api(write_options=SYNCHRONOUS)
        self.query_api = self.client.query_api()
        self.org = org
    
    def write_point(self, bucket: str, measurement: str, 
                   value: float, tags: Dict[str, str] = None, 
                   timestamp: datetime = None):
        """Write a single data point"""
        point = Point(measurement)
        
        if tags:
            for key, val in tags.items():
                point.tag(key, val)
        
        point.field("value", value)
        
        if timestamp:
            point.time(timestamp, WritePrecision.NS)
        
        self.write_api.write(bucket=bucket, org=self.org, record=point)
    
    def write_batch(self, bucket: str, points: List[Dict[str, Any]]):
        """Write multiple points efficiently"""
        influx_points = []
        
        for point_data in points:
            point = Point(point_data['measurement'])
            
            # Add tags
            for key, val in point_data.get('tags', {}).items():
                point.tag(key, val)
            
            # Add fields
            for key, val in point_data.get('fields', {}).items():
                point.field(key, val)
            
            # Add timestamp
            if 'timestamp' in point_data:
                point.time(point_data['timestamp'], WritePrecision.NS)
            
            influx_points.append(point)
        
        self.write_api.write(bucket=bucket, org=self.org, record=influx_points)
    
    def query_time_range(self, bucket: str, measurement: str, 
                        start_time: str, end_time: str, 
                        tags: Dict[str, str] = None) -> List[Dict]:
        """Query data for a time range"""
        query = f'''
        from(bucket: "{bucket}")
          |> range(start: {start_time}, stop: {end_time})
          |> filter(fn: (r) => r._measurement == "{measurement}")
        '''
        
        if tags:
            for key, value in tags.items():
                query += f'|> filter(fn: (r) => r.{key} == "{value}")\n'
        
        result = self.query_api.query(org=self.org, query=query)
        
        points = []
        for table in result:
            for record in table.records:
                points.append({
                    'time': record.get_time(),
                    'value': record.get_value(),
                    'measurement': record.get_measurement(),
                    'tags': {k: v for k, v in record.values.items() 
                            if k not in ['_time', '_value', '_measurement', '_field']}
                })
        
        return points
    
    def query_aggregated(self, bucket: str, measurement: str, 
                        window: str, aggregation: str = "mean",
                        start_time: str = "-1h") -> List[Dict]:
        """Query aggregated data with time windows"""
        query = f'''
        from(bucket: "{bucket}")
          |> range(start: {start_time})
          |> filter(fn: (r) => r._measurement == "{measurement}")
          |> aggregateWindow(every: {window}, fn: {aggregation}, createEmpty: false)
          |> yield(name: "{aggregation}")
        '''
        
        result = self.query_api.query(org=self.org, query=query)
        
        points = []
        for table in result:
            for record in table.records:
                points.append({
                    'time': record.get_time(),
                    'value': record.get_value(),
                    'window': window,
                    'aggregation': aggregation
                })
        
        return points
    
    def create_continuous_query(self, bucket: str, measurement: str):
        """Create continuous query for downsampling"""
        # Note: InfluxDB 2.x uses Tasks instead of Continuous Queries
        task_flux = f'''
        option task = {{name: "downsample_{measurement}", every: 1m}}
        
        from(bucket: "{bucket}")
          |> range(start: -1h)
          |> filter(fn: (r) => r._measurement == "{measurement}")
          |> aggregateWindow(every: 5m, fn: mean, createEmpty: false)
          |> to(bucket: "{bucket}_downsampled", org: "{self.org}")
        '''
        
        # Would need to use Tasks API to create this
        return task_flux
    
    def setup_retention_policy(self, bucket: str, retention_hours: int):
        """Setup data retention policy"""
        # InfluxDB 2.x uses bucket retention settings
        from influxdb_client.client.bucket_api import BucketApi
        
        bucket_api = BucketApi(self.client)
        
        # Update bucket with retention policy
        bucket_update = {
            "retentionRules": [
                {
                    "type": "expire",
                    "everySeconds": retention_hours * 3600
                }
            ]
        }
        
        # This would update the bucket retention
        return bucket_update

# Usage Example
def influxdb_example():
    # Initialize client
    influx = InfluxDBManager(
        url="http://localhost:8086",
        token="your-token-here",
        org="your-org"
    )
    
    # Write single point
    influx.write_point(
        bucket="metrics",
        measurement="cpu_usage",
        value=85.2,
        tags={"host": "web1", "env": "production"},
        timestamp=datetime.now(timezone.utc)
    )
    
    # Write batch of points
    batch_points = []
    for i in range(100):
        batch_points.append({
            'measurement': 'system_metrics',
            'tags': {
                'host': f'web{i % 3 + 1}',
                'env': 'production'
            },
            'fields': {
                'cpu_usage': random.uniform(20, 90),
                'memory_usage': random.uniform(30, 80),
                'disk_usage': random.uniform(10, 70)
            },
            'timestamp': datetime.now(timezone.utc)
        })
    
    influx.write_batch("metrics", batch_points)
    
    # Query recent data
    recent_data = influx.query_time_range(
        bucket="metrics",
        measurement="cpu_usage",
        start_time="-1h",
        end_time="now()",
        tags={"host": "web1"}
    )
    
    print(f"Found {len(recent_data)} data points")
    
    # Query aggregated data
    hourly_avg = influx.query_aggregated(
        bucket="metrics",
        measurement="cpu_usage",
        window="1h",
        aggregation="mean",
        start_time="-24h"
    )
    
    print(f"Hourly averages: {len(hourly_avg)} points")

# Advanced InfluxDB Patterns

class MetricsCollector:
    def __init__(self, influx_manager: InfluxDBManager):
        self.influx = influx_manager
        self.buffer = []
        self.buffer_size = 1000
    
    def collect_metric(self, measurement: str, value: float, 
                      tags: Dict[str, str] = None):
        """Collect metrics with buffering"""
        point = {
            'measurement': measurement,
            'fields': {'value': value},
            'tags': tags or {},
            'timestamp': datetime.now(timezone.utc)
        }
        
        self.buffer.append(point)
        
        if len(self.buffer) >= self.buffer_size:
            self.flush_buffer()
    
    def flush_buffer(self):
        """Flush buffered metrics to InfluxDB"""
        if self.buffer:
            self.influx.write_batch("metrics", self.buffer)
            self.buffer.clear()
    
    def collect_system_metrics(self):
        """Collect system metrics"""
        import psutil
        
        # CPU metrics
        self.collect_metric(
            "system_cpu",
            psutil.cpu_percent(),
            {"type": "usage", "host": "local"}
        )
        
        # Memory metrics
        memory = psutil.virtual_memory()
        self.collect_metric(
            "system_memory",
            memory.percent,
            {"type": "usage", "host": "local"}
        )
        
        # Disk metrics
        disk = psutil.disk_usage('/')
        self.collect_metric(
            "system_disk",
            (disk.used / disk.total) * 100,
            {"type": "usage", "host": "local", "mount": "/"}
        )

# Retention and Downsampling Strategy
def setup_data_lifecycle(influx: InfluxDBManager):
    """Setup comprehensive data lifecycle management"""
    
    # Raw data retention: 7 days
    raw_retention = {
        "bucket": "metrics_raw",
        "retention_hours": 24 * 7
    }
    
    # 5-minute aggregates: 30 days
    short_term_retention = {
        "bucket": "metrics_5m",
        "retention_hours": 24 * 30
    }
    
    # 1-hour aggregates: 1 year
    long_term_retention = {
        "bucket": "metrics_1h",
        "retention_hours": 24 * 365
    }
    
    # Downsampling tasks would be created here
    downsampling_tasks = [
        {
            "name": "downsample_5m",
            "source": "metrics_raw",
            "destination": "metrics_5m",
            "window": "5m",
            "aggregation": "mean"
        },
        {
            "name": "downsample_1h",
            "source": "metrics_5m", 
            "destination": "metrics_1h",
            "window": "1h",
            "aggregation": "mean"
        }
    ]
    
    return raw_retention, short_term_retention, long_term_retention, downsampling_tasks`}
          </pre>

          <h3 style={styles.heading}>Prometheus Time-Series Database</h3>
          <p style={styles.paragraph}>
            Prometheus is a monitoring-focused time-series database that excels at metrics collection 
            and alerting. It uses a pull-based model for data collection and provides powerful 
            querying capabilities through PromQL.
          </p>
          <pre style={styles.codeBlock}>
{`Prometheus Implementation:

# Prometheus Configuration
# prometheus.yml
global:
  scrape_interval: 15s
  evaluation_interval: 15s
  external_labels:
    cluster: 'production'
    replica: 'prometheus-1'

rule_files:
  - "recording_rules.yml"
  - "alert_rules.yml"

scrape_configs:
  # Application metrics
  - job_name: 'api-servers'
    static_configs:
      - targets: ['api1:8080', 'api2:8080', 'api3:8080']
    scrape_interval: 10s
    metrics_path: '/metrics'
    
  # Node exporter for system metrics
  - job_name: 'node-exporter'
    static_configs:
      - targets: ['node1:9100', 'node2:9100']
    
  # Custom application metrics
  - job_name: 'custom-metrics'
    static_configs:
      - targets: ['localhost:8000']

# Storage configuration
storage:
  tsdb:
    path: /prometheus/data
    retention.time: 30d
    retention.size: 100GB
    min-block-duration: 2h
    max-block-duration: 25h

# Python Prometheus Client
from prometheus_client import Counter, Histogram, Gauge, Summary, generate_latest
from prometheus_client import CollectorRegistry, multiprocess
import time
import threading
from typing import Dict, List
import requests

class PrometheusMetrics:
    def __init__(self, registry=None):
        self.registry = registry or CollectorRegistry()
        
        # Define metrics
        self.request_count = Counter(
            'http_requests_total',
            'Total HTTP requests',
            ['method', 'endpoint', 'status'],
            registry=self.registry
        )
        
        self.request_duration = Histogram(
            'http_request_duration_seconds',
            'HTTP request duration',
            ['method', 'endpoint'],
            buckets=[0.1, 0.25, 0.5, 1.0, 2.5, 5.0, 10.0],
            registry=self.registry
        )
        
        self.active_connections = Gauge(
            'active_connections',
            'Number of active connections',
            registry=self.registry
        )
        
        self.response_size = Summary(
            'http_response_size_bytes',
            'HTTP response size in bytes',
            ['endpoint'],
            registry=self.registry
        )
        
        # Business metrics
        self.orders_total = Counter(
            'orders_created_total',
            'Total orders created',
            ['product_category', 'payment_method'],
            registry=self.registry
        )
        
        self.revenue_total = Counter(
            'revenue_total_dollars',
            'Total revenue in dollars',
            ['product_category'],
            registry=self.registry
        )
        
        # System metrics
        self.cpu_usage = Gauge(
            'system_cpu_usage_percent',
            'CPU usage percentage',
            ['host'],
            registry=self.registry
        )
        
        self.memory_usage = Gauge(
            'system_memory_usage_bytes',
            'Memory usage in bytes',
            ['host', 'type'],
            registry=self.registry
        )
    
    def track_request(self, method: str, endpoint: str, status: int, duration: float):
        """Track HTTP request metrics"""
        self.request_count.labels(
            method=method,
            endpoint=endpoint,
            status=status
        ).inc()
        
        self.request_duration.labels(
            method=method,
            endpoint=endpoint
        ).observe(duration)
    
    def track_business_event(self, event_type: str, **labels):
        """Track business-specific events"""
        if event_type == 'order_created':
            self.orders_total.labels(
                product_category=labels.get('category', 'unknown'),
                payment_method=labels.get('payment_method', 'unknown')
            ).inc()
            
            self.revenue_total.labels(
                product_category=labels.get('category', 'unknown')
            ).inc(labels.get('amount', 0))
    
    def update_system_metrics(self, host: str, cpu_percent: float, 
                            memory_used: int, memory_total: int):
        """Update system metrics"""
        self.cpu_usage.labels(host=host).set(cpu_percent)
        self.memory_usage.labels(host=host, type='used').set(memory_used)
        self.memory_usage.labels(host=host, type='total').set(memory_total)
    
    def get_metrics(self) -> str:
        """Get metrics in Prometheus format"""
        return generate_latest(self.registry)

# Advanced Prometheus Patterns
class PrometheusCollector:
    def __init__(self, metrics: PrometheusMetrics):
        self.metrics = metrics
        self.collection_interval = 15  # seconds
        self.running = False
        self.thread = None
    
    def start_collection(self):
        """Start background metrics collection"""
        self.running = True
        self.thread = threading.Thread(target=self._collect_loop)
        self.thread.daemon = True
        self.thread.start()
    
    def stop_collection(self):
        """Stop background metrics collection"""
        self.running = False
        if self.thread:
            self.thread.join()
    
    def _collect_loop(self):
        """Background collection loop"""
        while self.running:
            try:
                self._collect_system_metrics()
                self._collect_application_metrics()
                time.sleep(self.collection_interval)
            except Exception as e:
                print(f"Error collecting metrics: {e}")
                time.sleep(self.collection_interval)
    
    def _collect_system_metrics(self):
        """Collect system-level metrics"""
        import psutil
        import socket
        
        hostname = socket.gethostname()
        
        # CPU metrics
        cpu_percent = psutil.cpu_percent(interval=1)
        
        # Memory metrics
        memory = psutil.virtual_memory()
        
        self.metrics.update_system_metrics(
            host=hostname,
            cpu_percent=cpu_percent,
            memory_used=memory.used,
            memory_total=memory.total
        )
    
    def _collect_application_metrics(self):
        """Collect application-specific metrics"""
        # This would collect custom application metrics
        # Example: database connection pool size, queue lengths, etc.
        pass

# PromQL Query Examples
class PrometheusQuerier:
    def __init__(self, prometheus_url: str):
        self.base_url = prometheus_url.rstrip('/')
    
    def query(self, query: str, time_param: str = None) -> Dict:
        """Execute PromQL query"""
        url = f"{self.base_url}/api/v1/query"
        params = {'query': query}
        
        if time_param:
            params['time'] = time_param
        
        response = requests.get(url, params=params)
        return response.json()
    
    def query_range(self, query: str, start: str, end: str, step: str) -> Dict:
        """Execute PromQL range query"""
        url = f"{self.base_url}/api/v1/query_range"
        params = {
            'query': query,
            'start': start,
            'end': end,
            'step': step
        }
        
        response = requests.get(url, params=params)
        return response.json()
    
    def get_request_rate(self, service: str = None, duration: str = '5m') -> Dict:
        """Get request rate for service"""
        if service:
            query = f'rate(http_requests_total{{service="{service}"}}[{duration}])'
        else:
            query = f'rate(http_requests_total[{duration}])'
        
        return self.query(query)
    
    def get_error_rate(self, service: str = None, duration: str = '5m') -> Dict:
        """Get error rate for service"""
        if service:
            query = f'''
            rate(http_requests_total{{service="{service}",status=~"5.."}[{duration}]) /
            rate(http_requests_total{{service="{service}"}}[{duration}])
            '''
        else:
            query = f'''
            rate(http_requests_total{{status=~"5.."}[{duration}]) /
            rate(http_requests_total[{duration}])
            '''
        
        return self.query(query)
    
    def get_p95_latency(self, service: str = None, duration: str = '5m') -> Dict:
        """Get 95th percentile latency"""
        if service:
            query = f'''
            histogram_quantile(0.95,
              rate(http_request_duration_seconds_bucket{{service="{service}"}}[{duration}])
            )
            '''
        else:
            query = f'''
            histogram_quantile(0.95,
              rate(http_request_duration_seconds_bucket[{duration}])
            )
            '''
        
        return self.query(query)
    
    def get_top_endpoints(self, limit: int = 10, duration: str = '1h') -> Dict:
        """Get top endpoints by request volume"""
        query = f'topk({limit}, sum by (endpoint) (rate(http_requests_total[{duration}])))'
        return self.query(query)

# Usage Example
def prometheus_example():
    # Initialize metrics
    metrics = PrometheusMetrics()
    
    # Start background collection
    collector = PrometheusCollector(metrics)
    collector.start_collection()
    
    # Simulate some requests
    metrics.track_request('GET', '/api/users', 200, 0.15)
    metrics.track_request('POST', '/api/orders', 201, 0.45)
    metrics.track_request('GET', '/api/products', 500, 1.2)
    
    # Track business events
    metrics.track_business_event('order_created', 
                               category='electronics', 
                               payment_method='credit_card',
                               amount=99.99)
    
    # Get metrics output
    metrics_output = metrics.get_metrics()
    print("Metrics for Prometheus:")
    print(metrics_output[:500] + "...")
    
    # Query Prometheus
    querier = PrometheusQuerier("http://localhost:9090")
    
    # Get request rate
    request_rate = querier.get_request_rate(duration='5m')
    print(f"Request rate: {request_rate}")
    
    # Get error rate
    error_rate = querier.get_error_rate(duration='5m')
    print(f"Error rate: {error_rate}")
    
    # Stop collection
    collector.stop_collection()

# Recording Rules for Performance
# recording_rules.yml
groups:
  - name: api_performance
    interval: 30s
    rules:
      # Pre-calculate request rate
      - record: api:request_rate_5m
        expr: rate(http_requests_total[5m])
      
      # Pre-calculate error rate
      - record: api:error_rate_5m
        expr: |
          rate(http_requests_total{status=~"5.."}[5m]) /
          rate(http_requests_total[5m])
      
      # Pre-calculate P95 latency
      - record: api:latency_p95_5m
        expr: |
          histogram_quantile(0.95,
            rate(http_request_duration_seconds_bucket[5m])
          )
      
      # Business metrics
      - record: business:orders_per_hour
        expr: increase(orders_created_total[1h])
      
      - record: business:revenue_per_hour
        expr: increase(revenue_total_dollars[1h])`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Storage Optimization Techniques</h2>
          
          <h3 style={styles.heading}>Compression and Encoding</h3>
          <p style={styles.paragraph}>
            Time-series databases employ sophisticated compression techniques to minimize storage 
            requirements while maintaining query performance. Understanding these techniques helps 
            in designing efficient data models and storage strategies.
          </p>
          <pre style={styles.codeBlock}>
{`Time-Series Compression Techniques:

1. DELTA ENCODING
Original Values: [100, 102, 105, 103, 108, 110]
Deltas:         [100, +2, +3, -2, +5, +2]
Savings: ~50% for trending data

Implementation:
class DeltaEncoder:
    def __init__(self):
        self.last_value = None
    
    def encode(self, values: List[float]) -> List[float]:
        if not values:
            return []
        
        encoded = [values[0]]  # First value as-is
        
        for i in range(1, len(values)):
            delta = values[i] - values[i-1]
            encoded.append(delta)
        
        return encoded
    
    def decode(self, encoded: List[float]) -> List[float]:
        if not encoded:
            return []
        
        decoded = [encoded[0]]
        
        for i in range(1, len(encoded)):
            value = decoded[i-1] + encoded[i]
            decoded.append(value)
        
        return decoded

2. DOUBLE DELTA ENCODING
Values:       [100, 102, 105, 103, 108, 110]
Deltas:       [100, +2, +3, -2, +5, +2]
Double Delta: [100, +2, +1, -5, +7, -3]
Savings: ~70% for smooth trends

3. GORILLA COMPRESSION (Facebook's Algorithm)
- XOR-based compression for floating-point values
- Exploits temporal locality in time-series data
- Achieves 12:1 compression ratios

class GorillaCompressor:
    def __init__(self):
        self.prev_value = 0
        self.prev_xor = 0
        self.leading_zeros = 0
        self.trailing_zeros = 0
    
    def compress_value(self, value: float) -> bytes:
        # Convert float to bits
        import struct
        bits = struct.unpack('>Q', struct.pack('>d', value))[0]
        
        # XOR with previous value
        xor = bits ^ self.prev_value
        
        if xor == 0:
            # Value unchanged - store single bit
            return b'0'
        
        # Count leading and trailing zeros
        leading = self._count_leading_zeros(xor)
        trailing = self._count_trailing_zeros(xor)
        
        # Compression logic based on XOR pattern
        if leading >= self.leading_zeros and trailing >= self.trailing_zeros:
            # Use previous window
            meaningful_bits = 64 - self.leading_zeros - self.trailing_zeros
            return self._encode_bits(xor, meaningful_bits)
        else:
            # New window
            self.leading_zeros = leading
            self.trailing_zeros = trailing
            return self._encode_new_window(xor, leading, trailing)
    
    def _count_leading_zeros(self, value: int) -> int:
        if value == 0:
            return 64
        return (value.bit_length() - 1) ^ 63
    
    def _count_trailing_zeros(self, value: int) -> int:
        if value == 0:
            return 64
        return (value & -value).bit_length() - 1

4. RUN-LENGTH ENCODING
For repeated values:
Values: [5, 5, 5, 5, 7, 7, 8, 8, 8]
RLE:    [(5, 4), (7, 2), (8, 3)]
Savings: ~60% for repeated values

class RunLengthEncoder:
    def encode(self, values: List[Any]) -> List[tuple]:
        if not values:
            return []
        
        encoded = []
        current_value = values[0]
        count = 1
        
        for i in range(1, len(values)):
            if values[i] == current_value:
                count += 1
            else:
                encoded.append((current_value, count))
                current_value = values[i]
                count = 1
        
        # Add the last run
        encoded.append((current_value, count))
        return encoded
    
    def decode(self, encoded: List[tuple]) -> List[Any]:
        decoded = []
        for value, count in encoded:
            decoded.extend([value] * count)
        return decoded

5. TIMESTAMP COMPRESSION
Timestamps are highly predictable in time-series data:

class TimestampCompressor:
    def __init__(self, base_timestamp: int, interval: int):
        self.base_timestamp = base_timestamp
        self.interval = interval  # Expected interval in seconds
    
    def compress_timestamps(self, timestamps: List[int]) -> List[int]:
        """Compress timestamps using base + offset"""
        compressed = []
        
        for ts in timestamps:
            # Calculate offset from base timestamp
            offset = ts - self.base_timestamp
            
            # If it's a regular interval, store as multiplier
            if offset % self.interval == 0:
                compressed.append(offset // self.interval)
            else:
                # Store actual offset for irregular timestamps
                compressed.append(offset)
        
        return compressed

6. DICTIONARY ENCODING
For tag values with limited cardinality:

class DictionaryEncoder:
    def __init__(self):
        self.string_to_id = {}
        self.id_to_string = {}
        self.next_id = 0
    
    def encode_string(self, value: str) -> int:
        if value not in self.string_to_id:
            self.string_to_id[value] = self.next_id
            self.id_to_string[self.next_id] = value
            self.next_id += 1
        
        return self.string_to_id[value]
    
    def decode_id(self, id: int) -> str:
        return self.id_to_string.get(id, "")

# Comprehensive Compression Strategy
class TimeSeriesCompressor:
    def __init__(self):
        self.delta_encoder = DeltaEncoder()
        self.rle_encoder = RunLengthEncoder()
        self.dict_encoder = DictionaryEncoder()
        self.timestamp_compressor = None
    
    def compress_series(self, data_points: List[Dict]) -> Dict:
        """Compress entire time series"""
        if not data_points:
            return {}
        
        # Extract components
        timestamps = [dp['timestamp'] for dp in data_points]
        values = [dp['value'] for dp in data_points]
        tags = [dp.get('tags', {}) for dp in data_points]
        
        # Compress timestamps
        base_timestamp = timestamps[0]
        interval = self._detect_interval(timestamps)
        self.timestamp_compressor = TimestampCompressor(base_timestamp, interval)
        compressed_timestamps = self.timestamp_compressor.compress_timestamps(timestamps)
        
        # Compress values using delta encoding
        compressed_values = self.delta_encoder.encode(values)
        
        # Compress tags using dictionary encoding
        compressed_tags = []
        for tag_dict in tags:
            compressed_tag = {}
            for key, value in tag_dict.items():
                compressed_tag[key] = self.dict_encoder.encode_string(value)
            compressed_tags.append(compressed_tag)
        
        return {
            'base_timestamp': base_timestamp,
            'interval': interval,
            'timestamps': compressed_timestamps,
            'values': compressed_values,
            'tags': compressed_tags,
            'dictionary': self.dict_encoder.id_to_string
        }
    
    def _detect_interval(self, timestamps: List[int]) -> int:
        """Detect common interval in timestamps"""
        if len(timestamps) < 2:
            return 1
        
        intervals = [timestamps[i] - timestamps[i-1] for i in range(1, len(timestamps))]
        
        # Find most common interval
        from collections import Counter
        interval_counts = Counter(intervals)
        return interval_counts.most_common(1)[0][0]

# Storage Layout Optimization
class TimeSeriesStorage:
    def __init__(self, block_size: int = 2 * 1024 * 1024):  # 2MB blocks
        self.block_size = block_size
        self.compressor = TimeSeriesCompressor()
    
    def store_series(self, series_id: str, data_points: List[Dict]) -> str:
        """Store compressed time series"""
        # Compress the data
        compressed_data = self.compressor.compress_series(data_points)
        
        # Calculate storage metrics
        original_size = self._calculate_original_size(data_points)
        compressed_size = self._calculate_compressed_size(compressed_data)
        compression_ratio = original_size / compressed_size if compressed_size > 0 else 0
        
        print(f"Series {series_id}:")
        print(f"  Original size: {original_size:,} bytes")
        print(f"  Compressed size: {compressed_size:,} bytes")
        print(f"  Compression ratio: {compression_ratio:.2f}:1")
        
        # In real implementation, would write to disk/database
        return f"stored_{series_id}"
    
    def _calculate_original_size(self, data_points: List[Dict]) -> int:
        """Estimate original data size"""
        # Rough estimation: timestamp (8) + value (8) + tags (variable)
        size = 0
        for dp in data_points:
            size += 16  # timestamp + value
            for key, value in dp.get('tags', {}).items():
                size += len(key) + len(str(value)) + 2  # key + value + overhead
        return size
    
    def _calculate_compressed_size(self, compressed_data: Dict) -> int:
        """Estimate compressed data size"""
        import json
        return len(json.dumps(compressed_data).encode())

# Usage Example
def compression_example():
    # Generate sample time series data
    import time
    import random
    
    base_time = int(time.time())
    data_points = []
    
    for i in range(1000):
        data_points.append({
            'timestamp': base_time + i * 60,  # 1-minute intervals
            'value': 50 + random.uniform(-5, 5) + 0.1 * i,  # Trending data
            'tags': {
                'host': f'server{i % 3 + 1}',
                'env': 'production',
                'service': 'api-gateway'
            }
        })
    
    # Store and compress
    storage = TimeSeriesStorage()
    storage_id = storage.store_series("cpu_usage_series", data_points)
    
    print(f"Stored as: {storage_id}")

# Run the example
compression_example()`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Design for your query patterns:</strong> Optimize your schema and indexing 
              strategy based on how you'll query the data, not just how you'll store it.
            </li>
            <li style={styles.listItem}>
              <strong>Manage cardinality carefully:</strong> High-cardinality tags can lead to 
              storage explosion. Use finite, predictable tag values and avoid user IDs as tags.
            </li>
            <li style={styles.listItem}>
              <strong>Implement proper retention policies:</strong> Set up automated data lifecycle 
              management with appropriate retention periods for different data resolutions.
            </li>
            <li style={styles.listItem}>
              <strong>Use downsampling strategies:</strong> Aggregate older data to reduce storage 
              costs while maintaining historical trends for analysis.
            </li>
            <li style={styles.listItem}>
              <strong>Monitor your monitoring system:</strong> Track the performance and resource 
              usage of your time-series database to prevent it from becoming a bottleneck.
            </li>
            <li style={styles.listItem}>
              <strong>Choose the right tool for your use case:</strong> Different time-series 
              databases excel in different scenarios - match the tool to your specific requirements.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            Time-series databases are specialized systems designed to handle the unique characteristics 
            of time-stamped data efficiently. They provide optimized storage, compression, and querying 
            capabilities that traditional databases cannot match for time-series workloads.
          </p>
          <p style={styles.paragraph}>
            Success with time-series databases requires understanding your data patterns, query 
            requirements, and retention needs. Proper schema design, compression strategies, and 
            lifecycle management are crucial for building scalable, cost-effective time-series 
            solutions that can handle massive data volumes while providing fast query performance.
          </p>
        </section>
      </main>
    </article>
  );
};

export default TimeSeriesDatabases;
